import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { num } from "@owners/format";
import { api } from "../api";
import { FOLDER_KEYS, FOLDER_LABELS, FilterButtons, StatusPill, periodLabel, shortDate, type Item } from "../components/Items";

/** Missing Data (SPEC §5) — HMA Users, in place of Awaiting Approval. */
export function MissingData() {
  const [folder, setFolder] = useState<string>("");
  const q = useQuery({
    queryKey: ["missing-data", folder],
    queryFn: () => api<{ items: Item[]; tiles: { missing: number; expired: number; due: number; nextDue: string | null; hotelsAffected: number } }>(
      `/api/missing-data${folder ? `?folder=${folder}` : ""}`),
  });
  const t = q.data?.tiles;
  const items = q.data?.items ?? [];

  return (
    <>
      <div className="eyebrow">Owners Module · Missing Data</div>
      <h1>{num(items.length)} items need an update across {num(t?.hotelsAffected ?? 0)} hotels</h1>
      <div className="row" style={{ alignItems: "stretch", marginBottom: 16 }}>
        <Tile label="Missing" value={t?.missing} />
        <Tile label="Expired" value={t?.expired} />
        <Tile label={t?.nextDue ? `Due by ${shortDate(t.nextDue).replace(/, \d{4}$/, "")}` : "Due"} value={t?.due} />
        <Tile label="Hotels affected" value={t?.hotelsAffected} />
      </div>
      <FilterButtons value={folder} onChange={setFolder}
        options={[["", "All folders"], ...FOLDER_KEYS.map((k) => [k, FOLDER_LABELS[k]] as [string, string])]} />
      <div className="card">
        {q.isLoading ? <p className="muted">Loading…</p> : items.length === 0 ? <p className="muted">Nothing outstanding. Thank you.</p> : (
          <table>
            <thead><tr><th>Item</th><th>Hotel</th><th>Status</th><th>Folder</th><th>Feeds</th><th /></tr></thead>
            <tbody>
              {items.map((i) => (
                <tr key={`${i.requirementId}`}>
                  <td>{i.label}{i.period ? ` · ${periodLabel(i.period)}` : ""}</td>
                  <td>{i.hotelName}</td>
                  <td><StatusPill s={i.status} dueOn={i.dueOn} /></td>
                  <td>{FOLDER_LABELS[i.folder]}</td>
                  <td className="muted">{i.feeds}</td>
                  <td>
                    <Link to={`/dataroom?hotel=${i.hotelId}&folder=${i.folder}&item=${i.requirementId}`}>Upload in Dataroom →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function Tile({ label, value }: { label: string; value: number | undefined }) {
  return (
    <div className="card" style={{ minWidth: 160, marginBottom: 0 }}>
      <div className="eyebrow">{label}</div>
      <div style={{ fontFamily: "var(--serif)", fontSize: 28, color: "var(--navy)" }}>{value == null ? "—" : num(value)}</div>
    </div>
  );
}
